// styled.js
import { styled } from '@mui/material/styles';
import { Card, Box, Chip } from '@mui/material';

// Honey colors (matching the original MembersTab theme)
const honeyColors = {
  primary: '#F1C40F',
  primaryLight: '#F4D03F',
  primaryDark: '#D4AC0D',
  amber: '#FFB74D',
  cream: '#FFF8E1',
  honeycomb: '#D4A574',
  surface: '#FFFFFF',
  surfaceHoney: '#FFF9E6',
  surfaceGlass: 'rgba(255, 255, 255, 0.85)',
  border: '#F5E6B3',
  borderLight: '#FAF0C4',
  textPrimary: '#3E2723',
  textSecondary: '#5D4037',
  textMuted: '#8D6E63',
  warning: '#FF9800',
  info: '#29B6F6',
  success: '#66BB6A',
  error: '#EF5350',
  // Advanced gradients
  gradientPrimary: 'linear-gradient(135deg, #F1C40F 0%, #FFB74D 100%)',
  gradientHoneycomb: 'linear-gradient(135deg, #D4A574 0%, #F1C40F 50%, #FFB74D 100%)',
  gradientCard: 'linear-gradient(145deg, rgba(255,255,255,0.9) 0%, rgba(255,249,230,0.8) 100%)',
  // Advanced shadows
  shadowLight: '0 2px 8px rgba(212, 165, 116, 0.12)',
  shadowMedium: '0 4px 12px rgba(212, 165, 116, 0.18)',
  shadowHeavy: '0 8px 24px rgba(212, 165, 116, 0.25)',
  shadowGlow: '0 0 20px rgba(241, 196, 15, 0.3)',
  // Glass morphism
  backdropBlur: 'blur(20px)',
  backdropBlurLight: 'blur(10px)',
};

export const StyledMemberCard = styled(Card)(({ compact }) => ({
  borderRadius: '20px',
  background: honeyColors.gradientCard,
  backdropFilter: honeyColors.backdropBlur,
  border: `2px solid ${honeyColors.borderLight}`,
  boxShadow: honeyColors.shadowLight,
  transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
  height: '100%',
  position: 'relative',
  overflow: 'hidden',
  cursor: 'pointer',
  
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: '4px',
    background: honeyColors.gradientPrimary,
    opacity: 0,
    transition: 'opacity 0.3s ease',
  },
  
  '&:hover': {
    transform: 'translateY(-4px)',
    borderColor: honeyColors.border,
    boxShadow: honeyColors.shadowHeavy,

    '&::before': {
      opacity: 1,
    },
  },
  
  '& .MuiCardContent-root': {
    position: 'relative',
    zIndex: 1,
    padding: compact ? '16px !important' : '24px !important',
  },
}));

export const MemberAvatarContainer = styled(Box)(({ compact }) => ({
  position: 'relative',
  display: 'inline-block',
  
  '& .MuiAvatar-root': {
    width: compact ? '48px' : '64px',
    height: compact ? '48px' : '64px',
    border: `3px solid ${honeyColors.surface}`,
    boxShadow: honeyColors.shadowLight,
    transition: 'all 0.3s ease',
    
    '&.online': {
      borderColor: honeyColors.success,
      boxShadow: `${honeyColors.shadowLight}, 0 0 0 2px rgba(102, 187, 106, 0.3)`,
    },
  },
  
  // Online indicator dot
  '&::after': {
    content: '""',
    position: 'absolute',
    bottom: compact ? '2px' : '4px',
    right: compact ? '2px' : '4px',
    width: compact ? '12px' : '16px',
    height: compact ? '12px' : '16px',
    backgroundColor: honeyColors.success,
    border: `2px solid ${honeyColors.surface}`,
    borderRadius: '50%',
    opacity: 0,
    transition: 'opacity 0.3s ease',
  },
  
  '&.online::after': {
    opacity: 1,
  },
}));

export const RoleBadge = styled(Chip)(({ role }) => {
  const roleStyles = {
    'FO': { // Founder
      background: 'rgba(251, 191, 36, 0.15)',
      color: '#f59e0b',
      border: '1px solid rgba(251, 191, 36, 0.3)',
    },
    'AD': { // Admin
      background: 'rgba(239, 83, 80, 0.15)',
      color: honeyColors.error,
      border: `1px solid rgba(239, 83, 80, 0.3)`,
    },
    'MA': { // Manager
      background: 'rgba(156, 39, 176, 0.15)',
      color: '#9c27b0',
      border: `1px solid rgba(156, 39, 176, 0.3)`,
    },
    'PA': { // Participant/Member
      background: 'rgba(102, 187, 106, 0.15)',
      color: honeyColors.success,
      border: `1px solid rgba(102, 187, 106, 0.3)`,
    },
    'GU': { // Guest
      background: honeyColors.surfaceHoney,
      color: honeyColors.textSecondary,
      border: `1px solid ${honeyColors.border}`,
    },
  };
  
  const style = roleStyles[role] || roleStyles.PA;
  
  return {
    backgroundColor: style.background,
    color: style.color,
    border: style.border,
    fontWeight: 600,
    fontSize: '0.75rem',
    height: '28px',
    borderRadius: '14px',
    
    '& .MuiChip-label': {
      padding: '0 12px',
    },
  };
});

export const MemberInfoSection = styled(Box)({
  '& .member-name': {
    fontWeight: 600,
    color: honeyColors.textPrimary,
    fontSize: '1.1rem',
    marginBottom: '4px',
    lineHeight: 1.3,
    display: '-webkit-box',
    WebkitLineClamp: 1,
    WebkitBoxOrient: 'vertical',
    overflow: 'hidden',
  },
  
  '& .member-status': {
    fontSize: '0.85rem',
    marginBottom: '8px',
    
    '&.online': {
      color: honeyColors.success,
      fontWeight: 600,
    },
    
    '&.offline': {
      color: honeyColors.textMuted,
    },
  },
  
  '& .member-bio': {
    color: honeyColors.textSecondary,
    fontSize: '0.9rem',
    lineHeight: 1.4,
    marginBottom: '12px',
    display: '-webkit-box',
    WebkitLineClamp: 2,
    WebkitBoxOrient: 'vertical',
    overflow: 'hidden',
  },
  
  '& .member-detail': {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    marginBottom: '8px',
    fontSize: '0.85rem',
    color: honeyColors.textMuted,
    
    '& .MuiSvgIcon-root': {
      fontSize: '1rem',
    },
    
    '& .MuiTypography-root': {
      fontSize: '0.85rem',
      color: honeyColors.textMuted,
    },
  },
});

export const ActionButtonsContainer = styled(Box)({
  display: 'flex',
  gap: '8px',
  marginTop: '16px',
  paddingTop: '16px',
  borderTop: `1px solid ${honeyColors.borderLight}`,
  
  '& .MuiIconButton-root': {
    backgroundColor: honeyColors.surfaceHoney,
    border: `1px solid ${honeyColors.borderLight}`,
    borderRadius: '12px',
    width: '40px',
    height: '40px',
    transition: 'all 0.2s ease',
    color: honeyColors.textSecondary,
    
    '&:hover': {
      backgroundColor: honeyColors.cream,
      borderColor: honeyColors.border,
      transform: 'scale(1.1)',
      color: honeyColors.textPrimary,
    },
    
    '&.primary': {
      backgroundColor: honeyColors.primary,
      color: honeyColors.textPrimary,
      borderColor: 'transparent',
      
      '&:hover': {
        backgroundColor: honeyColors.primaryDark,
      },
    },
    
    '&:disabled': {
      opacity: 0.4,
      cursor: 'not-allowed',
    },
  },
  
  '& .MuiButton-root': {
    borderRadius: '12px',
    textTransform: 'none',
    fontWeight: 600,
    fontSize: '0.75rem',
    padding: '6px 16px',
    
    '&.MuiButton-containedSuccess': {
      background: 'linear-gradient(135deg, #66BB6A 0%, #4CAF50 100%)',
      border: 'none',
      color: '#fff',
      
      '&:hover': {
        background: 'linear-gradient(135deg, #4CAF50 0%, #388E3C 100%)',
      },
    },
  },
});